import React, { createContext, useContext, useEffect, useState, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export type Direction = 'ltr' | 'rtl'
export type Locale = 'en-US' | 'en-GB' | 'pt-PT' | 'ar-SA' | 'he-IL' | 'es-ES' | 'fr-FR' | 'de-DE'

interface LocalizationContextType {
  locale: Locale
  direction: Direction
  setLocale: (locale: Locale) => void
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string
  formatTime: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string
  formatNumber: (number: number, options?: Intl.NumberFormatOptions) => string
  formatCurrency: (amount: number, currency?: string, options?: Intl.NumberFormatOptions) => string
  formatRelativeTime: (date: Date | string) => string
  isRTL: boolean
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined)

// RTL languages mapping
const RTL_LANGUAGES: Record<string, boolean> = {
  ar: true,    // Arabic
  'ar-SA': true, // Arabic (Saudi Arabia)
  he: true,    // Hebrew
  'he-IL': true, // Hebrew (Israel)
  fa: true,    // Persian
  ur: true,    // Urdu
}

// Get direction based on locale
const getDirection = (locale: string): Direction => {
  const langCode = locale.split('-')[0]
  return RTL_LANGUAGES[locale] || RTL_LANGUAGES[langCode] ? 'rtl' : 'ltr'
}

interface LocalizationProviderProps {
  children: ReactNode
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const { i18n } = useTranslation()
  const [locale, setLocaleState] = useState<Locale>((i18n.language as Locale) || 'en-US')
  const [direction, setDirection] = useState<Direction>(getDirection(locale))

  // Create Intl.RelativeTimeFormat instance
  const relativeTimeFormatter = useMemo(() => {
    try {
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' })
    } catch (error) {
      // Fallback to English if locale not supported
      return new Intl.RelativeTimeFormat('en-US', { numeric: 'auto', style: 'long' })
    }
  }, [locale])

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale)
    setDirection(getDirection(newLocale))
    i18n.changeLanguage(newLocale)
  }

  // Update HTML attributes when direction changes
  useEffect(() => {
    document.documentElement.setAttribute('lang', locale)
    document.documentElement.setAttribute('dir', direction)
  }, [locale, direction])

  // Intl.DateTimeFormat wrapper
  const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return ''
    
    try {
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options
      }).format(dateObj)
    } catch (error) {
      // Fallback to English formatting
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options
      }).format(dateObj)
    }
  }

  // Intl.DateTimeFormat wrapper for time
  const formatTime = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return ''
    
    try {
      return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        ...options
      }).format(dateObj)
    } catch (error) {
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        ...options
      }).format(dateObj)
    }
  }

  // Intl.NumberFormat wrapper
  const formatNumber = (number: number, options?: Intl.NumberFormatOptions): string => {
    try {
      return new Intl.NumberFormat(locale, options).format(number)
    } catch (error) {
      return new Intl.NumberFormat('en-US', options).format(number)
    }
  }

  // Intl.NumberFormat wrapper for currency
  const formatCurrency = (amount: number, currency = 'USD', options?: Intl.NumberFormatOptions): string => {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        ...options
      }).format(amount)
    } catch (error) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        ...options
      }).format(amount)
    }
  }

  // Enhanced relative time formatting using Intl.RelativeTimeFormat
  const formatRelativeTime = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    if (isNaN(dateObj.getTime())) return ''

    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

    // Future dates
    if (diffInSeconds < 0) {
      return relativeTimeFormatter.format(Math.ceil(diffInSeconds / 60), 'minute')
    }

    // Less than a minute
    if (diffInSeconds < 60) {
      return relativeTimeFormatter.format(0, 'second')
    }

    // Minutes
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
      return relativeTimeFormatter.format(-diffInMinutes, 'minute')
    }

    // Hours
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
      return relativeTimeFormatter.format(-diffInHours, 'hour')
    }

    // Days
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) {
      return relativeTimeFormatter.format(-diffInDays, 'day')
    }

    // Weeks
    const diffInWeeks = Math.floor(diffInDays / 7)
    if (diffInWeeks < 4) {
      return relativeTimeFormatter.format(-diffInWeeks, 'week')
    }

    // Months
    const diffInMonths = Math.floor(diffInDays / 30)
    if (diffInMonths < 12) {
      return relativeTimeFormatter.format(-diffInMonths, 'month')
    }

    // Years
    const diffInYears = Math.floor(diffInDays / 365)
    return relativeTimeFormatter.format(-diffInYears, 'year')
  }

  const contextValue: LocalizationContextType = {
    locale,
    direction,
    setLocale,
    formatDate,
    formatTime,
    formatNumber,
    formatCurrency,
    formatRelativeTime,
    isRTL: direction === 'rtl'
  }

  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  )
}

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext)
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider')
  }
  return context
}

export default LocalizationContext